import { useState, useEffect, useMemo } from 'react';
import { useUser } from '../hooks/useUser';

import { getCountryCode } from 'shared/src/getCountryCode';

import { default as Table, type TableProps } from 'antd/es/table';
import { default as Button } from 'antd/es/button';
import { goToCustomerApp } from '../utils/goToCustomerApp';
import {
  type VirtualBankAccount,
  fetchVirtualBanks
} from '../network/fetchVirtualAccounts';
import { generateFilters } from '../utils/antd/tableFilter';
import { Resource } from '../components/Resource';
import { FormattedProduct } from '../utils/FormattedProduct';

export function VirtualBanksPage({ filePath }: { filePath: string }) {
  const [virtualBanks, setVirtualBanks] = useState<VirtualBankAccount[]>([]);

  const [loadingVirtualBanks, setLoadingVirtualBanks] = useState(false);

  const { currentOrganisation } = useUser();

  useEffect(() => {
    const getVirtualBanks = async () => {
      setLoadingVirtualBanks(true);

      if (!currentOrganisation) {
        setLoadingVirtualBanks(false);
        return;
      }

      try {
        const virtualBanks = await fetchVirtualBanks(
          currentOrganisation.id.toString()
        );

        if (virtualBanks?.data) {
          setVirtualBanks(virtualBanks?.data);
        }
      } catch (error) {
        console.error('Error fetching virtual banks:', error);
      } finally {
        setLoadingVirtualBanks(false);
      }
    };

    getVirtualBanks();
  }, [currentOrganisation]);

  const columns: TableProps<VirtualBankAccount>['columns'] = useMemo(
    () => [
      {
        title: 'Name',
        width: '20%',
        dataIndex: 'name',
        render: (name, p) => (
          <FormattedProduct
            label={name}
            countryIso={getCountryCode(p.currency_code)}
          />
        ),
        ...generateFilters(virtualBanks, 'name')
      },
      {
        title: 'Account number',
        dataIndex: 'account_number',
        width: '20%',
        ...generateFilters(virtualBanks, 'account_number')
      },
      {
        title: 'CURRENCY',
        width: '20%',
        dataIndex: 'currency_code',
        ...generateFilters(virtualBanks, 'currency_code')
      }
    ],
    [virtualBanks]
  );

  return (
    <div>
      <Resource resource={{ filePath }} />
      {!currentOrganisation ? (
        <div className="flex">
          <Button
            data-testid="log-in-from-virtual-banks"
            onClick={() => goToCustomerApp()}
            type="primary"
          >
            Log in to see your virtual bank accounts
          </Button>
        </div>
      ) : (
        <Table
          rowKey="account_number"
          columns={columns}
          pagination={{ pageSize: 20 }}
          dataSource={virtualBanks}
          loading={loadingVirtualBanks}
          size="small"
        />
      )}
    </div>
  );
}
